import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LiveMessagingService } from '@headpower/layout';
import { forkJoin, Subject, throwError, catchError, takeUntil } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';

import { ExternalUnitImportDialogData, ExternalUnitImportImportUnitType, ExternalUnitImportRequest, SimplePriceList } from '../external-request.model';
import { ExternalRequestService } from '../external-request.service';
import { SelectListService } from '../../../settings/components/common/select-list-management/select-lists.service';
import { ListType, SelectListItem } from '../../../settings/components/common/select-list-management/select-list.model';
import { ExternalRequest, ExternalRequestTargetType } from '../external-request.model';

interface ImportUnitTypeData {
    type: ExternalUnitImportImportUnitType;
    checked: boolean;
}

@Component({
    selector: 'app-external-unit-import-dialog',
    templateUrl: './external-unit-import-dialog.component.html',
    styleUrls: ['./external-unit-import-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUnitImportDialogComponent implements OnInit, OnDestroy {

    public mobile: boolean = false;
    public initializing: boolean = true;
    public submitting: boolean = false;

    public targetId!: string;
    public targetType!: ExternalRequestTargetType;

    public importTargetTypes: SelectListItem[] = [];
    public priceLists: SimplePriceList[] = [];
    public externalUnitImports: ExternalRequest[] = [];

    public importTargetId: string = '';
    public importTargetType?: SelectListItem;
    public importPriceList?: SimplePriceList;
    public importUnitTypes: ImportUnitTypeData[] = [];

    // Common data
    public budgetsLocked!: boolean;

    // Task data
    public plannedAmountsLocked?: boolean;
    public isOrderer?: boolean;
    public isSupplier?: boolean;

    private currentCompanyId!: string;

    public get areAllImportUnitTypesNotSelectable(): boolean {
        if (this.targetType === 'Task') {
            return this.budgetsLocked &&
                this.plannedAmountsLocked &&
                !this.isOrderer &&
                !this.isSupplier;
        }
        else if (this.targetType === 'Worksite') {
            return this.budgetsLocked;
        }

        return true;
    }

    public get isFormValid(): boolean {
        return !!this.importTargetId &&
            !!this.importTargetType &&
            !!this.importPriceList &&
            this.importUnitTypes.some(o => o.checked);
    }

    private destroy$: Subject<void> = new Subject();

    constructor(
        private dialogRef: MatDialogRef<ExternalUnitImportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private dialogData: ExternalUnitImportDialogData,
        private oAuthService: OAuthService,
        private externalRequestService: ExternalRequestService,
        private selectListService: SelectListService,
        private liveMessagingService: LiveMessagingService,
        private cdRef: ChangeDetectorRef) { }

    ngOnInit() {
        this.currentCompanyId = this.oAuthService.getIdentityClaims()['headpower:company_uuid'];

        this.mobile = this.dialogData.mobile;
        this.targetId = this.dialogData.targetId;
        this.targetType = this.dialogData.targetType;
        this.importTargetId = this.dialogData.nisLink ? this.dialogData.nisLink : '';

        // Common data for all targets
        this.budgetsLocked = this.dialogData.targetData.budgetsLocked;

        // Task data
        if (this.targetType === 'Task') {
            this.plannedAmountsLocked = this.dialogData.targetData.plannedAmountsLocked;
            this.isOrderer = this.dialogData.targetData.isOrderer;
            this.isSupplier = this.dialogData.targetData.isSupplier;
        }

        this.fetchInitialData();
        this.buildImportUnitTypes();
        this.listenLiveMessages();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public isImportUnitTypeDisabled(type: ExternalUnitImportImportUnitType) {
        if (this.targetType === 'Task') {
            switch (type) {
                case 'Budgeted':
                    return this.budgetsLocked || this.importUnitTypes.some(o => o.type !== 'Budgeted' && o.checked);
                case 'Planned':
                    return this.plannedAmountsLocked || this.importUnitTypes.find(o => o.type === 'Budgeted').checked;
                case 'Ordered':
                    return !this.isOrderer || this.importUnitTypes.find(o => o.type === 'Budgeted').checked;
                case 'Supplied':
                    return !this.isSupplier || this.importUnitTypes.find(o => o.type === 'Budgeted').checked;
            }
        }
        else if (this.targetType === 'Worksite') {
            switch (type) {
                case 'Budgeted':
                case 'Planned':
                case 'Ordered':
                case 'Supplied':
                    return true;
            }
        }

        return true;
    }

    public getImportUnitTypeTooltip(type: ExternalUnitImportImportUnitType) {
        let tooltip: string;

        if (this.targetType === 'Task') {
            if (type !== 'Budgeted') {
                this.importUnitTypes.find(o => o.type === 'Budgeted').checked && (tooltip = 'default.externalUnitImport.budgetedUnitTypeSelected');
            }

            switch (type) {
                case 'Budgeted':
                    this.importUnitTypes.some(o => o.type !== 'Budgeted' && o.checked) && (tooltip = 'default.externalUnitImport.otherUnitTypesSelected');
                    this.budgetsLocked && (tooltip = 'default.externalUnitImport.targetBudgetLocked');
                    break;
                case 'Planned':
                    this.plannedAmountsLocked && (tooltip = 'default.externalUnitImport.targetUnitPlannedAmountsLocked');
                    break;
                case 'Ordered':
                    !this.isOrderer && (tooltip = 'default.externalUnitImport.companyNotOrderer');
                    break;
                case 'Supplied':
                    !this.isSupplier && (tooltip = 'default.externalUnitImport.companyNotSupplier');
                    break;
            }
        }

        // No tooltips for worksite

        return tooltip;
    }

    public submit() {
        if (!this.isFormValid) {
            return;
        }

        this.submitting = true;

        const config: ExternalUnitImportRequest = {
            TargetId: this.targetId,
            TargetType: this.targetType,
            ImportTargetId: this.importTargetId,
            ImportTargetType: this.importTargetType.Value,
            ImportPriceListId: this.importPriceList.Id,
            ImportUnitType: this.importUnitTypes
                .filter(o => o.checked)
                .map(o => o.type)
                .join(',')
        };

        this.externalRequestService.requestExternalUnitImport(config)
            .pipe(
                catchError(error => {
                    this.submitting = false;
                    this.cdRef.markForCheck();

                    return throwError(() => error);
                })
            )
            .subscribe(() => {
                this.dialogRef.close();
            });
    }

    private fetchInitialData() {
        forkJoin([
            this.selectListService.getListValues(ListType.ExternalUnitImportTargetType),
            this.externalRequestService.getAvailablePriceLists(),
            this.externalRequestService.getExternalRequestHistory(this.targetType, this.targetId, 'ExternalUnitImport', this.currentCompanyId)
        ])
            .subscribe(([importTargetTypes, priceLists, externalUnitImports]) => {
                this.importTargetTypes = importTargetTypes;
                this.priceLists = priceLists;
                this.externalUnitImports = externalUnitImports;

                this.initializing = false;
                this.cdRef.markForCheck();
            });
    }

    private fetchExternalUnitImportHistory() {
        this.externalRequestService.getExternalRequestHistory(this.targetType, this.targetId, 'ExternalUnitImport', this.currentCompanyId)
            .subscribe(result => {
                this.externalUnitImports = result;
                this.cdRef.markForCheck();
            });
    }

    private buildImportUnitTypes() {
        if (this.targetType === 'Task') {
            this.importUnitTypes = [
                { type: 'Budgeted', checked: false },
                { type: 'Planned', checked: false },
                { type: 'Ordered', checked: false },
                { type: 'Supplied', checked: false }
            ];
        }
        else if (this.targetType === 'Worksite') {
            this.importUnitTypes = [
                { type: 'Budgeted', checked: !this.budgetsLocked },
                { type: 'Planned', checked: false },
                { type: 'Ordered', checked: false },
                { type: 'Supplied', checked: false }
            ];
        }

        this.cdRef.markForCheck();
    }

    private listenLiveMessages() {
        const topic = `iwm/externalRequest/externalUnitImport/${this.targetId}`;

        this.liveMessagingService.listenToSingleMessages(topic)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                // If request changes refresh the history
                this.fetchExternalUnitImportHistory();
            });
    }
}
